import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  Save, Shield, ArrowUpDown, Percent, Lock, Anchor, TrendingDown, Layers,
  Sparkles, Info, Plus, Trash2,
} from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const NumField = ({ label, hint, value, onChange, min, max, step = 0.1, testId, suffix = "%", disabled = false }) => (
  <div>
    <label className="vx-label block mb-1">{label}</label>
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        data-testid={testId}
        className="vx-input h-9 text-sm w-32 font-mono disabled:opacity-40"
      />
      <span className="text-xs text-[#6b7280] font-mono">{suffix}</span>
    </div>
    {hint && <div className="text-[10px] text-[#6b7280] mt-1 max-w-xs">{hint}</div>}
  </div>
);

const DEFAULT_TM = {
  break_even_enabled: false,
  break_even_trigger_pips: 12,
  break_even_offset_pips: 2,
  trailing_stop_enabled: false,
  trailing_stop_trigger_pips: 15,
  trailing_stop_distance_pips: 10,
  partial_close_enabled: false,
  partial_close_levels: [],
  auto_be_after_partial: true,
};

// ---- Trade Management card (Phase 2.1) ----
const TmCard = ({ tm, setTm, preview, previewSymbol, setPreviewSymbol, previewLot, setPreviewLot, previewSlPips, setPreviewSlPips }) => {
  const setTM = (k, v) => setTm((t) => ({ ...t, [k]: v }));
  const addLevel = () => {
    const next = [...(tm.partial_close_levels || []), { trigger_pips: 20, close_pct: 33 }].slice(0, 3);
    setTM("partial_close_levels", next);
  };
  const setLevel = (i, k, v) => {
    const next = [...(tm.partial_close_levels || [])];
    next[i] = { ...next[i], [k]: Number(v) };
    setTM("partial_close_levels", next);
  };
  const removeLevel = (i) => {
    const next = [...(tm.partial_close_levels || [])];
    next.splice(i, 1);
    setTM("partial_close_levels", next);
  };

  const perPip = preview?.per_pip_usd ?? 0;
  // NB: live estimates should reflect the user's IN-PROGRESS form state,
  // not the currently-persisted TM. So we compute from `tm.*` × perPip
  // instead of trusting `preview.trade_management_usd` (which is server-side
  // effective, and only refreshes on Save).

  return (
    <div className="vx-card p-6 space-y-6" data-testid="tm-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#a78bfa] font-bold">
            <Sparkles className="w-3.5 h-3.5" /> Trade Management
            <span className="text-[9px] font-mono uppercase tracking-wider text-[#6b7280] border border-[#1f2937] rounded px-1.5 py-0.5 normal-case tracking-normal">Phase 2.1</span>
          </div>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
            Active management applied after entry: Break-Even auto-move, Trailing Stop, and Partial Close ladder.
            The EA persists these into the position comment so state survives EA restarts.
          </p>
        </div>
        <div className="min-w-[220px]">
          <div className="vx-label mb-1.5">Live $ preview</div>
          <div className="flex items-center gap-1.5 mb-2">
            <select value={previewSymbol} onChange={(e) => setPreviewSymbol(e.target.value)} className="vx-input h-8 text-xs w-24" data-testid="preview-symbol">
              {["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "US30", "BTCUSD"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="number" value={previewLot} onChange={(e) => setPreviewLot(Number(e.target.value))} step={0.01} min={0.01} className="vx-input h-8 text-xs w-16 font-mono" data-testid="preview-lot" />
            <span className="text-[10px] text-[#6b7280] font-mono">lots</span>
          </div>
          <div className="text-[11px] font-mono text-[#9ca3af]">
            1 pip @ {previewSymbol} × {previewLot} lot ={" "}
            <span className="text-[#10b981] font-bold" data-testid="preview-per-pip">${perPip.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Break Even */}
      <div className="border-t border-[#1f2937] pt-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Anchor className="w-4 h-4 text-[#10b981]" />
            <span className="text-sm font-semibold">Break-Even</span>
            <span className="text-[10px] text-[#6b7280] font-mono">move SL to entry once in profit</span>
          </div>
          <Switch
            checked={!!tm.break_even_enabled}
            onCheckedChange={(v) => setTM("break_even_enabled", v)}
            data-testid="tm-be-toggle"
          />
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${!tm.break_even_enabled ? "opacity-50" : ""}`}>
          <NumField label="Trigger" value={tm.break_even_trigger_pips} onChange={(v) => setTM("break_even_trigger_pips", v)}
            min={0.5} max={1000} step={0.5} suffix="pips" disabled={!tm.break_even_enabled} testId="tm-be-trigger" />
          <NumField label="Offset from entry" value={tm.break_even_offset_pips} onChange={(v) => setTM("break_even_offset_pips", v)}
            min={0} max={100} step={0.5} suffix="pips" disabled={!tm.break_even_enabled} testId="tm-be-offset"
            hint="e.g. +2 pips locks 2 pips of profit at BE." />
          <div className="rounded border border-[#1f2937] p-3 bg-[#0a0e1a]">
            <div className="vx-label mb-1">Live estimate</div>
            <div className="font-mono text-xs text-[#9ca3af]">
              Triggers @ <span className="text-[#10b981]" data-testid="tm-be-trig-usd">${(perPip * tm.break_even_trigger_pips).toFixed(2)}</span> profit ·
              locks <span className="text-white">${(perPip * tm.break_even_offset_pips).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Trailing Stop */}
      <div className="border-t border-[#1f2937] pt-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-[#3b82f6]" />
            <span className="text-sm font-semibold">Trailing Stop</span>
            <span className="text-[10px] text-[#6b7280] font-mono">arm at trigger, trail by distance</span>
          </div>
          <Switch
            checked={!!tm.trailing_stop_enabled}
            onCheckedChange={(v) => setTM("trailing_stop_enabled", v)}
            data-testid="tm-ts-toggle"
          />
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${!tm.trailing_stop_enabled ? "opacity-50" : ""}`}>
          <NumField label="Trigger" value={tm.trailing_stop_trigger_pips} onChange={(v) => setTM("trailing_stop_trigger_pips", v)}
            min={0.5} max={1000} step={0.5} suffix="pips" disabled={!tm.trailing_stop_enabled} testId="tm-ts-trigger" />
          <NumField label="Trail distance" value={tm.trailing_stop_distance_pips} onChange={(v) => setTM("trailing_stop_distance_pips", v)}
            min={0.5} max={500} step={0.5} suffix="pips" disabled={!tm.trailing_stop_enabled} testId="tm-ts-distance"
            hint="SL trails price by this many pips (only tightens)." />
          <div className="rounded border border-[#1f2937] p-3 bg-[#0a0e1a]">
            <div className="vx-label mb-1">Live estimate</div>
            <div className="font-mono text-xs text-[#9ca3af]">
              Arms @ <span className="text-[#3b82f6]" data-testid="tm-ts-trig-usd">${(perPip * tm.trailing_stop_trigger_pips).toFixed(2)}</span> ·
              trail <span className="text-white">${(perPip * tm.trailing_stop_distance_pips).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Partial Close */}
      <div className="border-t border-[#1f2937] pt-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#f59e0b]" />
            <span className="text-sm font-semibold">Partial Close</span>
            <span className="text-[10px] text-[#6b7280] font-mono">scale out at up to 3 levels</span>
          </div>
          <Switch
            checked={!!tm.partial_close_enabled}
            onCheckedChange={(v) => setTM("partial_close_enabled", v)}
            data-testid="tm-pc-toggle"
          />
        </div>
        <div className={`space-y-2 ${!tm.partial_close_enabled ? "opacity-50" : ""}`}>
          {(tm.partial_close_levels || []).map((lv, i) => {
            const trigUsd = perPip * (lv.trigger_pips || 0);
            const closedUsd = trigUsd * ((lv.close_pct || 0) / 100);
            return (
              <div key={i} className="grid grid-cols-[auto_1fr_1fr_auto_auto] gap-3 items-end p-3 rounded border border-[#1f2937] bg-[#0a0e1a]" data-testid={`tm-pc-level-${i}`}>
                <div className="text-[10px] font-mono uppercase text-[#a78bfa] font-bold pb-2">L{i + 1}</div>
                <div>
                  <label className="vx-label block mb-1">Trigger</label>
                  <div className="flex items-center gap-1">
                    <input type="number" value={lv.trigger_pips} onChange={(e) => setLevel(i, "trigger_pips", e.target.value)}
                      min={0.5} max={1000} step={0.5} disabled={!tm.partial_close_enabled}
                      className="vx-input h-8 text-xs w-20 font-mono" data-testid={`tm-pc-trig-${i}`} />
                    <span className="text-[10px] text-[#6b7280] font-mono">pips</span>
                  </div>
                </div>
                <div>
                  <label className="vx-label block mb-1">Close</label>
                  <div className="flex items-center gap-1">
                    <input type="number" value={lv.close_pct} onChange={(e) => setLevel(i, "close_pct", e.target.value)}
                      min={1} max={100} step={1} disabled={!tm.partial_close_enabled}
                      className="vx-input h-8 text-xs w-20 font-mono" data-testid={`tm-pc-pct-${i}`} />
                    <span className="text-[10px] text-[#6b7280] font-mono">%</span>
                  </div>
                </div>
                <div className="text-[10px] font-mono text-[#9ca3af] pb-2 text-right">
                  <div>@${trigUsd.toFixed(2)}</div>
                  <div className="text-[#f59e0b]">≈${closedUsd.toFixed(2)}</div>
                </div>
                <button onClick={() => removeLevel(i)} disabled={!tm.partial_close_enabled}
                  data-testid={`tm-pc-remove-${i}`}
                  className="w-8 h-8 rounded border border-[#1f2937] hover:border-[#ef4444] hover:bg-[#ef4444]/10 flex items-center justify-center text-[#ef4444] disabled:opacity-30">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
          {(tm.partial_close_levels || []).length < 3 && (
            <button onClick={addLevel} disabled={!tm.partial_close_enabled}
              data-testid="tm-pc-add"
              className="h-9 px-3 rounded border border-dashed border-[#1f2937] hover:border-[#a78bfa] hover:bg-[#a78bfa]/10 text-xs font-mono text-[#a78bfa] flex items-center gap-2 disabled:opacity-40">
              <Plus className="w-3.5 h-3.5" /> Add level {((tm.partial_close_levels || []).length || 0) + 1}
            </button>
          )}
        </div>

        <div className={`mt-4 flex items-center justify-between rounded border border-[#1f2937] bg-[#0a0e1a] p-3 ${!tm.partial_close_enabled ? "opacity-50" : ""}`}>
          <div className="flex items-center gap-2">
            <Info className="w-3.5 h-3.5 text-[#6b7280]" />
            <span className="text-xs text-[#9ca3af]">Auto-move SL to break-even after any partial close</span>
          </div>
          <Switch
            checked={!!tm.auto_be_after_partial}
            onCheckedChange={(v) => setTM("auto_be_after_partial", v)}
            disabled={!tm.partial_close_enabled}
            data-testid="tm-abp-toggle"
          />
        </div>
      </div>

      {/* SL preview + summary */}
      <div className="border-t border-[#1f2937] pt-4 flex items-end justify-between gap-4 flex-wrap">
        <div className="text-xs font-mono text-[#9ca3af]">
          Baseline risk @ <span className="text-white">{previewSlPips}p SL</span> ={" "}
          <span className="text-[#ef4444] font-bold" data-testid="tm-risk-usd">${(preview?.risk_at_sl_usd ?? 0).toFixed(2)}</span>{" "}
          ({preview?.risk_pct_of_balance ?? 0}% of ${preview?.balance ?? 0})
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-[#6b7280] font-mono">SL pips</label>
          <input type="number" value={previewSlPips} onChange={(e) => setPreviewSlPips(Number(e.target.value))}
            min={0} max={1000} step={1} data-testid="preview-slpips"
            className="vx-input h-8 text-xs w-20 font-mono" />
        </div>
      </div>
    </div>
  );
};


export default function Risk() {
  const [settings, setSettings] = useState(null);
  const [tm, setTm] = useState(DEFAULT_TM);
  const [saving, setSaving] = useState(false);
  const [balance, setBalance] = useState(10000);
  const [preview, setPreview] = useState(null);
  const [previewSymbol, setPreviewSymbol] = useState("EURUSD");
  const [previewLot, setPreviewLot] = useState(0.10);
  const [previewSlPips, setPreviewSlPips] = useState(20);

  useEffect(() => {
    api.get("/risk/settings").then((r) => {
      setSettings(r.data);
      setTm({ ...DEFAULT_TM, ...(r.data.trade_management || {}) });
    }).catch(() => toast.error("Failed to load risk settings"));
    api.get("/portfolio/risk-status").then((r) => setBalance(r.data.balance || 10000)).catch(() => {});
  }, []);

  const fetchPreview = useCallback(async () => {
    try {
      const r = await api.get(`/execution/preview?symbol=${previewSymbol}&lot=${previewLot}&sl_pips=${previewSlPips}`);
      setPreview(r.data);
    } catch (e) {
      // silent — preview is best-effort
    }
  }, [previewSymbol, previewLot, previewSlPips]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(
        Object.entries(settings).filter(([k, v]) =>
          v !== "" && v !== null && !["user_id", "updated_at", "account_id", "trade_management"].includes(k)
        )
      );
      clean.trade_management = tm;
      await api.put("/risk/settings", clean);
      toast.success("Risk settings saved");
      fetchPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const riskAmt = useMemo(() => balance * (settings?.risk_per_trade_pct || 0) / 100, [balance, settings]);
  const previewLots = useMemo(() => (riskAmt / (20 * 10)).toFixed(2), [riskAmt]);

  if (!settings) return <div className="text-[#9ca3af] font-mono text-sm">Loading…</div>;

  return (
    <div className="max-w-5xl space-y-6" data-testid="risk-page">
      <div>
        <div className="vx-label">Risk Engine</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">Risk Configuration</h1>
        <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
          Every generated signal is evaluated against these limits before being sent to the EA. Tighten these for a prop-firm challenge; loosen for a personal account.
        </p>
      </div>

      <div className="vx-card p-6 space-y-6" data-testid="risk-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#3b82f6] font-bold">
              <Percent className="w-3.5 h-3.5" /> Sizing
            </div>
            <NumField label="Risk per trade" value={settings.risk_per_trade_pct} onChange={(v) => set("risk_per_trade_pct", v)} min={0.1} max={5} step={0.1} testId="rf-risk-per-trade" />
            <NumField label="Min confidence to execute" value={settings.min_confidence_to_execute} onChange={(v) => set("min_confidence_to_execute", v)} min={0} max={100} step={1} suffix="/100" testId="rf-min-conf" hint="Signals below this score become advisory only (never auto-executed)." />
            <NumField label="Margin utilization cap" value={settings.margin_utilization_max_pct} onChange={(v) => set("margin_utilization_max_pct", v)} min={5} max={95} step={1} testId="rf-margin-cap" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#ef4444] font-bold">
              <Shield className="w-3.5 h-3.5" /> Guardrails
            </div>
            <NumField label="Daily loss limit" value={settings.daily_loss_limit_pct} onChange={(v) => set("daily_loss_limit_pct", v)} min={0.5} max={20} step={0.5} testId="rf-daily-loss" hint="Blocks new signals when daily P&L falls below this." />
            <NumField label="Max drawdown (from peak)" value={settings.max_drawdown_pct} onChange={(v) => set("max_drawdown_pct", v)} min={1} max={50} step={0.5} testId="rf-max-dd" />
            <NumField label="Equity kill switch" value={settings.equity_kill_switch_pct} onChange={(v) => set("equity_kill_switch_pct", v)} min={1} max={100} step={1} testId="rf-kill" hint="Halt trading if equity drops X% below day-start." />
          </div>
        </div>

        <div className="border-t border-[#1f2937] pt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#a78bfa] font-bold md:col-span-3">
            <ArrowUpDown className="w-3.5 h-3.5" /> Position Limits
          </div>
          <NumField label="Max concurrent positions" value={settings.max_concurrent_positions} onChange={(v) => set("max_concurrent_positions", v)} min={1} max={50} step={1} suffix="positions" testId="rf-max-conc" />
          <NumField label="Max per symbol" value={settings.max_positions_per_symbol} onChange={(v) => set("max_positions_per_symbol", v)} min={1} max={10} step={1} suffix="positions" testId="rf-max-per-sym" />
          <NumField label="Max per correlation group" value={settings.max_positions_per_correlation_group} onChange={(v) => set("max_positions_per_correlation_group", v)} min={1} max={10} step={1} suffix="positions" testId="rf-max-per-corr" />
        </div>

        <div className="border-t border-[#1f2937] pt-6 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#10b981] font-bold">
              <Lock className="w-3.5 h-3.5" /> Auto-Execute
            </div>
            <p className="text-sm text-[#9ca3af] mt-1 max-w-md">
              When ON, the EA places trades automatically for APPROVED signals. When OFF, signals are advisory only (EA logs alerts).
            </p>
          </div>
          <Switch
            checked={!!settings.auto_execute_enabled}
            onCheckedChange={(v) => set("auto_execute_enabled", v)}
            data-testid="rf-auto-execute"
          />
        </div>
      </div>

      <TmCard
        tm={tm} setTm={setTm} preview={preview}
        previewSymbol={previewSymbol} setPreviewSymbol={setPreviewSymbol}
        previewLot={previewLot} setPreviewLot={setPreviewLot}
        previewSlPips={previewSlPips} setPreviewSlPips={setPreviewSlPips}
      />

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="text-sm">
          <div className="vx-label mb-1">Baseline preview</div>
          <div className="text-[#9ca3af] font-mono text-xs">
            Balance <span className="text-white">${balance.toLocaleString()}</span> · Risk <span className="text-white">{settings.risk_per_trade_pct}%</span> = <span className="text-white">${riskAmt.toFixed(2)}</span> · EURUSD 20-pip SL → <span className="text-[#3b82f6] font-bold" data-testid="preview-lots">{previewLots} lots</span>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="risk-save-btn"
          className="h-10 px-5 rounded-md bg-[#3b82f6] hover:bg-[#2563eb] transition-colors font-semibold text-sm flex items-center gap-2 disabled:opacity-60 active:scale-[0.98]"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
